import React, {useCallback, useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Button, Space, Tabs, message} from 'antd';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {HasResource} from '../../../components/HasResource';
import {ResourceCode} from '../../../constants/resourceCodes';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelsDownloadModal from '../../central/parcels/components/ParcelsDownloadModal';
import {useDownloadCooldown} from '../../../hooks/useDownloadCooldown';
import {getUserInfo} from '../../../utils/auth';
import {showError} from '../../../utils/exceptionUtils';
import PopReviewDetailDrawer from './components/PopReviewDetailDrawer';
import PopReviewFilter from './components/PopReviewFilter';
import PopReviewTable from './components/PopReviewTable';
import {
    GET_POP_REVIEW_COUNT,
    GetPopReviewCountResponse
} from './graphql/queries/getPopReviewCount';
import {
    GENERATE_POP_REVIEW_DOWNLOAD_REPORT,
    GeneratePopReviewDownloadReportResponse,
    GeneratePopReviewDownloadReportVariables
} from './graphql/mutations/popReviewMutations';
import {
    PopReviewFilters,
    PopReviewScene,
    PopReviewTabType,
    PopReviewType
} from './graphql/types/PopReviewType';
import {usePopReviewOptions} from './hooks/usePopReviewOptions';
import {buildFiltersInputFromFilters, usePopReviewQuery} from './hooks/usePopReviewQuery';

/**
 * 默认筛选：提交时间必填，默认最近一个月。
 * 必填是性能兜底 —— 列表主查询按 submitted_at 排序，无时间条件的全量扫描在数据量上来后很危险。
 */
const getDefaultFilters = (): PopReviewFilters => ({
    submittedAt: [dayjs().subtract(1, 'month').startOf('day'), dayjs().endOf('day')]
});

/** 计数查询公共选项：每次都走网络，审完后 refetch 才能拿到最新值 */
const COUNT_QUERY_OPTIONS = {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only'
} as const;

/**
 * 揽收 POP 审核工作台
 *
 * 一级页签为入审场景（揽收成功 / 揽收失败），二级页签为待审核 / 已审核。
 * 二级页签与「审核状态」筛选以 AND 组合，二者矛盾时（如待审核 + 合格）返回空结果。
 * 批量审核为纯前端交互：Drawer 内逐条审、审完跳下一条，服务端只提供单条接口。
 */
const PopReviewPage: React.FC = () => {
    const {t, i18n} = useTranslation();

    const [activeScene, setActiveScene] = useState<PopReviewScene>(PopReviewScene.PICKUP_SUCCESS);
    const [activeTab, setActiveTab] = useState<PopReviewTabType>(PopReviewTabType.NEW);
    const [tempFilters, setTempFilters] = useState<PopReviewFilters>(getDefaultFilters());
    const [filters, setFilters] = useState<PopReviewFilters>(getDefaultFilters());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<PopReviewType[]>([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [reviewIds, setReviewIds] = useState<string[]>([]);

    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const {downloadLoading, setDownloadLoading, cooldownRemaining, getLastDownloadTime, setLastDownloadTime} =
        useDownloadCooldown(`popReviewDownloadLastTime_${getUserInfo()?.account?.id}`);
    const [generatePopReviewDownloadReport] = useMutation<
        GeneratePopReviewDownloadReportResponse,
        GeneratePopReviewDownloadReportVariables
    >(GENERATE_POP_REVIEW_DOWNLOAD_REPORT);

    const {popReviewData, loading, executeQuery, totalElements} = usePopReviewQuery();
    // 传 tempFilters.pickupHubId：站点下拉随用户当前选择即时联动，不等点「查询」
    const {
        reasonOptions,
        reasonTextByCode,
        disqualifyReasons,
        hubNameById,
        pickupSites,
        pickupDrivers,
        pickupSiteNameById,
        pickupDriverNameById,
        loading: optionsLoading
    } = usePopReviewOptions(tempFilters.pickupHubId, activeScene);

    const pickupSiteOptions = pickupSites.map(site => ({value: site.id, label: site.name}));
    const pickupDriverOptions = pickupDrivers.map(driver => ({
        value: driver.id,
        label: driver.name
    }));

    // 一级页签角标：两个场景各自的待审核数。用已提交的 filters 而非 tempFilters，与列表保持同源
    const {
        data: successPendingData,
        loading: successPendingLoading,
        refetch: refetchSuccessPending
    } = useQuery<GetPopReviewCountResponse>(GET_POP_REVIEW_COUNT, {
        ...COUNT_QUERY_OPTIONS,
        variables: {
            filters: buildFiltersInputFromFilters(
                filters,
                PopReviewTabType.NEW,
                PopReviewScene.PICKUP_SUCCESS
            )
        }
    });
    const {
        data: failedPendingData,
        loading: failedPendingLoading,
        refetch: refetchFailedPending
    } = useQuery<GetPopReviewCountResponse>(GET_POP_REVIEW_COUNT, {
        ...COUNT_QUERY_OPTIONS,
        variables: {
            filters: buildFiltersInputFromFilters(
                filters,
                PopReviewTabType.NEW,
                PopReviewScene.PICKUP_FAILED
            )
        }
    });
    // 已审核数：仅当前在待审核页签时需要单独查；在已审核页签时复用列表 totalElements
    const {
        data: reviewedCountData,
        loading: reviewedCountLoading,
        refetch: refetchReviewedCount
    } = useQuery<GetPopReviewCountResponse>(GET_POP_REVIEW_COUNT, {
        ...COUNT_QUERY_OPTIONS,
        variables: {
            filters: buildFiltersInputFromFilters(filters, PopReviewTabType.TERMINATED, activeScene)
        },
        skip: activeTab === PopReviewTabType.TERMINATED
    });

    const successPendingCount = successPendingData?.getPopReviewCount;
    const failedPendingCount = failedPendingData?.getPopReviewCount;
    const scenePendingCount =
        activeScene === PopReviewScene.PICKUP_SUCCESS ? successPendingCount : failedPendingCount;
    const scenePendingLoading =
        activeScene === PopReviewScene.PICKUP_SUCCESS
            ? successPendingLoading
            : failedPendingLoading;

    const refetchCounts = useCallback(() => {
        void refetchSuccessPending();
        void refetchFailedPending();
        if (activeTab !== PopReviewTabType.TERMINATED) {
            void refetchReviewedCount();
        }
    }, [refetchSuccessPending, refetchFailedPending, refetchReviewedCount, activeTab]);

    const clearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    const reload = useCallback(
        (
            nextFilters: PopReviewFilters,
            page: number,
            size: number,
            tab: PopReviewTabType,
            scene: PopReviewScene
        ) => {
            executeQuery(nextFilters, page, size, tab, scene);
            clearSelection();
        },
        [executeQuery]
    );

    // 首屏与场景 / Tab 切换时回到第一页重查
    useEffect(() => {
        setCurrentPage(1);
        reload(filters, 1, pageSize, activeTab, activeScene);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, activeScene]);

    // 两个场景的不合格原因字典不同，切场景时清空该筛选，其余筛选保留
    const handleSceneChange = (scene: PopReviewScene) => {
        setTempFilters(prev => ({...prev, disqualifyReasonCodes: undefined}));
        setFilters(prev => ({...prev, disqualifyReasonCodes: undefined}));
        setActiveScene(scene);
    };

    const handleSearch = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
        reload(tempFilters, 1, pageSize, activeTab, activeScene);
    };

    const handleReset = () => {
        const defaults = getDefaultFilters();
        setTempFilters(defaults);
        setFilters(defaults);
        setCurrentPage(1);
        reload(defaults, 1, pageSize, activeTab, activeScene);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
        reload(filters, page, size, activeTab, activeScene);
    };

    // 点某一行：只打开该条（对齐 POD）
    const showDetail = (record: PopReviewType) => {
        setReviewIds([record.reviewTaskId]);
        setDrawerVisible(true);
    };

    // 批量审核：只在选中的行之间移动
    const handleBatchReview = () => {
        if (selectedRows.length === 0) {
            message.warning(t('popReview.messages.selectAtLeastOne'));
            return;
        }
        setReviewIds(selectedRows.map(row => row.reviewTaskId));
        setDrawerVisible(true);
    };

    // 单条审完即刷新，不等 Drawer 关闭
    const handleReviewed = () => {
        reload(filters, currentPage, pageSize, activeTab, activeScene);
        refetchCounts();
    };

    /** 导出当前场景已审核数据（异步生成，去下载中心领取）；用已生效的 filters */
    const handleExport = useCallback(async () => {
        const currentTime = Date.now();
        if (currentTime - getLastDownloadTime() < 5000 || downloadLoading) {
            message.warning(t('popReview.export.generating'));
            return;
        }
        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);
            // 带了 tab 与场景，返回值必不为空；?? {} 仅为满足类型
            const exportFilters =
                buildFiltersInputFromFilters(filters, PopReviewTabType.TERMINATED, activeScene) ?? {};
            // 后端按 language 出三语表头/文案；取当前 i18n 短码（zh/en/es）
            const language = i18n.language.split('-')[0];
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await generatePopReviewDownloadReport({
                variables: {filters: exportFilters, language, timezone}
            });
            const result = response.data?.generatePopReviewDownloadReport;
            if (result?.success && result.fileName) {
                setGeneratedFileName(result.fileName);
                setDownloadModalVisible(true);
            } else if (result?.errorCode?.startsWith('EXCEED_MAX_LIMIT')) {
                const max = result.errorCode.split(':')[1] ?? '';
                message.error(t('popReview.export.exceedMaxLimit', {max}));
            } else if (result?.errorCode === 'NO_DATA') {
                message.warning(t('popReview.export.noData'));
            } else {
                message.error(t('popReview.export.failed'));
            }
        } catch (e) {
            // 透传后端 messageKey（重复提交 / 权限 / 系统异常），无可识别 key 时回落通用提示
            showError(e);
            setDownloadLoading(false);
        }
    }, [
        downloadLoading,
        getLastDownloadTime,
        setDownloadLoading,
        setLastDownloadTime,
        filters,
        activeScene,
        i18n.language,
        generatePopReviewDownloadReport,
        t
    ]);

    // Tab 角标：计数加载中显示 '...'，避免闪 0 造成「真的没有数据」的误读
    const getTabLabel = (labelKey: string, count?: number, isLoading?: boolean) =>
        `${t(labelKey)} (${isLoading || count == null ? '...' : count})`;

    /**
     * 当前 Tab 用列表的 totalElements，非当前 Tab 用独立计数查询。
     * 当前 Tab 必须以列表 loading 为准：切 Tab 后新列表返回前 totalElements 仍是旧值，
     * 直接显示会短暂错位。
     */
    const getCountProps = (tab: PopReviewTabType): [number | undefined, boolean] => {
        if (tab === activeTab) {
            return [totalElements, loading];
        }
        return tab === PopReviewTabType.NEW
            ? [scenePendingCount, scenePendingLoading]
            : [reviewedCountData?.getPopReviewCount, reviewedCountLoading];
    };

    const sceneTabs = [
        {
            key: PopReviewScene.PICKUP_SUCCESS,
            label: getTabLabel(
                'popReview.scene.PICKUP_SUCCESS',
                successPendingCount,
                successPendingLoading
            )
        },
        {
            key: PopReviewScene.PICKUP_FAILED,
            label: getTabLabel(
                'popReview.scene.PICKUP_FAILED',
                failedPendingCount,
                failedPendingLoading
            )
        }
    ];

    const tabs = [
        {
            key: PopReviewTabType.NEW,
            label: getTabLabel('popReview.tab.new', ...getCountProps(PopReviewTabType.NEW))
        },
        {
            key: PopReviewTabType.TERMINATED,
            label: getTabLabel(
                'popReview.tab.terminated',
                ...getCountProps(PopReviewTabType.TERMINATED)
            )
        }
    ];

    return (
        <div className="p-4">
            <Tabs
                activeKey={activeScene}
                onChange={key => handleSceneChange(key as PopReviewScene)}
                items={sceneTabs}
                className="[&_.ant-tabs-tab]:font-bold"
            />

            <PopReviewFilter
                filters={tempFilters}
                onChange={setTempFilters}
                reasonOptions={reasonOptions}
                pickupSiteOptions={pickupSiteOptions}
                pickupDriverOptions={pickupDriverOptions}
            />

            {/*
                Tab 与按钮合并为一行：POP 只有 2 个短 Tab，分两行会留下大片空白。
                Tab 语义是对筛选结果的再分类，非页面级模式切换，故切 Tab 保留筛选条件。
            */}
            <div className="flex justify-between items-center mb-3">
                <Tabs
                    activeKey={activeTab}
                    onChange={key => setActiveTab(key as PopReviewTabType)}
                    items={tabs}
                    className="[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-tab]:font-bold"
                />
                <Space>
                    {activeTab === PopReviewTabType.NEW && (
                        <HasResource resourceCodes={[ResourceCode.cs.CS_POP_REVIEW_OPERATE]}>
                            <Button
                                type="primary"
                                onClick={handleBatchReview}
                                disabled={selectedRows.length === 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                    {t('popReview.action.batchReview', {count: selectedRows.length})}
                                </span>
                            </Button>
                        </HasResource>
                    )}
                    {activeTab === PopReviewTabType.TERMINATED && (
                        <Button
                            type="primary"
                            onClick={() => void handleExport()}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `${t('popReview.export.button')} ${cooldownRemaining}s`
                                    : t('popReview.export.button')}
                            </span>
                        </Button>
                    )}
                    <Button
                        type="primary"
                        loading={loading || optionsLoading}
                        onClick={handleSearch}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('popReview.action.filter')}</span>
                    </Button>
                    <Button onClick={handleReset} className="w-auto min-w-24">
                        <span className="font-bold">{t('common.reset')}</span>
                    </Button>
                </Space>
            </div>

            <div className="mb-4">
                <PopReviewTable
                    popReviewData={popReviewData}
                    loading={loading}
                    showDetail={showDetail}
                    onSelectChange={(keys, rows) => {
                        setSelectedRowKeys(keys);
                        setSelectedRows(rows);
                    }}
                    selectedRowKeys={selectedRowKeys}
                    hubNameById={hubNameById}
                    pickupSiteNameById={pickupSiteNameById}
                    pickupDriverNameById={pickupDriverNameById}
                    reasonTextByCode={reasonTextByCode}
                    scene={activeScene}
                    showReviewColumns={activeTab === PopReviewTabType.TERMINATED}
                    showRowSelection={activeTab === PopReviewTabType.NEW}
                />
            </div>

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />

            <PopReviewDetailDrawer
                open={drawerVisible}
                onClose={() => {
                    // 对齐 POD：关闭时清 reviewIds 与选中态并刷新列表，
                    // 否则列表行仍处选中态，再点「批量审核」会带出已审过的最后一条
                    setDrawerVisible(false);
                    setReviewIds([]);
                    // reload 内含 clearSelection（清 selectedRowKeys/selectedRows）+ executeQuery
                    reload(filters, currentPage, pageSize, activeTab, activeScene);
                    refetchCounts();
                }}
                reviewIds={reviewIds}
                onReviewed={handleReviewed}
                reasonOptions={reasonOptions}
                reasonTextByCode={reasonTextByCode}
                disqualifyReasons={disqualifyReasons}
                hubNameById={hubNameById}
                pickupSiteNameById={pickupSiteNameById}
                pickupDriverNameById={pickupDriverNameById}
            />

            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default PopReviewPage;
